import { createContext, ReactNode, useContext, useReducer } from "react";

interface MDSModalRenderLockClaim {
  canRender: boolean;
  ownerId: string | null;
}

interface MDSModalRenderLock {
  claimMDSModalRenderSlot: (modalId: string) => MDSModalRenderLockClaim;
  releaseMDSModalRenderSlot: (modalId: string) => void;
  requestMDSModalRenderLockReset: () => void;
}

const defaultModalRenderLock: MDSModalRenderLock = {
  claimMDSModalRenderSlot: () => ({ canRender: true, ownerId: null }),
  releaseMDSModalRenderSlot: () => {},
  requestMDSModalRenderLockReset: () => {},
};

const MDSModalRenderLockContext = createContext<MDSModalRenderLock>(defaultModalRenderLock);

export const MDSModalRenderLockProvider = ({ children }: { children: ReactNode }) => {
  const [, forceRender] = useReducer(count => count + 1, 0);
  // Intentionally recreated every provider render: the first open MDSModal to claim
  // this render pass is the only one allowed to open its React Aria ModalOverlay.
  const state: { renderedMDSModalId: string | null } = { renderedMDSModalId: null };

  const claimMDSModalRenderSlot = (modalId: string): MDSModalRenderLockClaim => {
    if (state.renderedMDSModalId === null) {
      state.renderedMDSModalId = modalId;
      return { canRender: true, ownerId: modalId };
    }

    return {
      canRender: state.renderedMDSModalId === modalId,
      ownerId: state.renderedMDSModalId,
    };
  };

  const releaseMDSModalRenderSlot = (modalId: string) => {
    if (state.renderedMDSModalId !== modalId) {
      return;
    }

    state.renderedMDSModalId = null;
    forceRender();
  };

  const requestMDSModalRenderLockReset = () => {
    forceRender();
  };

  return (
    <MDSModalRenderLockContext.Provider
      value={{
        claimMDSModalRenderSlot,
        releaseMDSModalRenderSlot,
        requestMDSModalRenderLockReset,
      }}
    >
      {children}
    </MDSModalRenderLockContext.Provider>
  );
};

export const useMDSModalRenderLock = () => useContext(MDSModalRenderLockContext);
